/* eslint-disable @typescript-eslint/no-use-before-define */
/* eslint-disable @typescript-eslint/no-shadow */
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
  styled as styledMUI,
  useTheme
} from '@mui/material';
import Drawer from '@mui/material/Drawer';
import { alpha } from '@mui/material/styles';
import Icon from 'components/Icon/Icon';
import { useLogoContrastStyle } from 'hooks/useLogoContrastStyle';
import { partnerAssets } from 'partners/partnersAssets';
import { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from 'store/hooks';
import { useProduct } from 'themes/ProductThemeProvider';
import {
  PRODUCTS,
  PRODUCT_TYPE_BY_PRODUCT,
  productSegmentFromPath,
  type TProductName
} from 'themes/tokens/productTheme';
import {
  TNavData_SideBar,
  TProps_SideBar,
  TSpaceDevicesState,
  TSubScriptionState
} from 'typings';
import { QUERY_PARAMS, ROUTES } from 'utils/constants';
import { setProductTypeStorage } from 'utils/sessionFunctions';
import {
  PRODUCT_LABEL_KEY,
  findProductNavKeyForPath,
  getGlobalNavData,
  getProductNavData,
  getRouteForProductNavKey
} from './productNavConfig';

const drawerWidth = isMobile ? '100%' : 340;

const DrawerHeader = styledMUI('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '16px 20px',
  backgroundColor: theme.palette.background.default,
  borderBottom: `1px solid ${theme.palette.border.divider}`,
  minHeight: theme.spacing(10),

  // necessary for content to be below app bar
  ...theme.mixins.toolbar
}));

const SECTION_LABEL_SX = {
  textTransform: 'uppercase' as const,
  letterSpacing: '0.08em',
  opacity: 0.7
};

function SideBar(props: TProps_SideBar) {
  const { setOpenSideBar, openSideBar } = props;
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { t } = useTranslation('translation', {
    keyPrefix: 'layout.sidebar'
  });
  const Logo = partnerAssets.logo;
  const logoContrastStyle = useLogoContrastStyle();

  const themeProduct = useProduct();
  const {
    homeSecuritySubscriptionList,
    birdSubscriptionList,
    petSubscriptionList,
    trailSubscriptionList,
    babySubscriptionList
  } = useAppSelector<TSubScriptionState>((state) => state.subscription);

  const subscriptionIdByProduct: Record<TProductName, string | undefined> = {
    home: homeSecuritySubscriptionList?.[0]?.id,
    bird: birdSubscriptionList?.[0]?.id,
    pet: petSubscriptionList?.[0]?.id,
    trail: trailSubscriptionList?.[0]?.id,
    baby: babySubscriptionList?.[0]?.id
  };

  const { spaceDevices } = useAppSelector<TSpaceDevicesState>(
    (state) => state.spaceDevices
  );
  // Data Plans only makes sense for a product the account actually owns a
  // 4G-capable camera for — today that's only ever Home or Trail devices,
  // but checking the device data directly (rather than hardcoding those two
  // products) means this keeps working if another product line ships a 4G
  // SKU later.
  const hasFourGDeviceByProduct = (product: TProductName) =>
    spaceDevices?.some(
      (device) =>
        device.four_g &&
        device.model_product_type === PRODUCT_TYPE_BY_PRODUCT[product]
    ) ?? false;

  const [selectedProduct, setSelectedProduct] = useState<TProductName>(
    productSegmentFromPath(location.pathname) ?? themeProduct
  );

  // Re-sync whenever the route itself changes — either it now says which
  // product (routeLockedProduct wins) or it doesn't, in which case follow
  // whatever RouteProductTheme resolved (session/Redux/default). Does NOT
  // re-run on the forced same-path refresh below (see onProductSelect):
  // `location.pathname` as a string is unchanged then, so a manual pick
  // never gets snapped back by this effect.
  useEffect(() => {
    setSelectedProduct(
      productSegmentFromPath(location.pathname) ?? themeProduct
    );
  }, [location.pathname, themeProduct]);

  const productNavData = getProductNavData(
    selectedProduct,
    subscriptionIdByProduct[selectedProduct],
    hasFourGDeviceByProduct(selectedProduct),
    t
  );
  const globalNavData = getGlobalNavData(t);
  const allNavData = [...productNavData, ...globalNavData];

  const index = allNavData.findIndex((el) => {
    const found = el.macthRoutes.find((route) => {
      const res = location.pathname.search(route);
      return res >= 0;
    });
    return found;
  });
  const [selectedNav, setSelectedNav] = useState<TNavData_SideBar>(
    allNavData[index]
  );

  useEffect(() => {
    setSelectedNav(allNavData[index]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, selectedProduct]);

  const onSideNavClick = (nav: TNavData_SideBar) => {
    setSelectedNav(nav);
    navigate(nav?.link);
    if (isMobile) {
      setOpenSideBar(false);
    }
  };

  const onProductSelect = (product: TProductName) => {
    // Same sessionStorage key the app-bridge writes to at /app-bridge, so a
    // manual in-app switch and an app-bridge-supplied product_type are one
    // unified signal — see RouteProductTheme/resolveThemeProduct.
    setProductTypeStorage(
      QUERY_PARAMS.PRODUCT_TYPE_QUERY,
      PRODUCT_TYPE_BY_PRODUCT[product]
    );
    setSelectedProduct(product);

    // On a page with a per-product equivalent (Upsell, Devices, ...), follow
    // it there — picking Pet while on Home's Upsell page should land on
    // Pet's Upsell page, not just re-tint the Home page you're still on.
    const navKey = findProductNavKeyForPath(selectedProduct, location.pathname);
    if (navKey) {
      navigate(
        getRouteForProductNavKey(
          navKey,
          product,
          subscriptionIdByProduct[product]
        )
      );
      return;
    }

    // No per-product equivalent for the current page (FAQ, My Order, Payment
    // Method, ...) — just re-theme in place. Forces RouteProductTheme
    // (subscribed to useLocation()) to recompute, since the sessionStorage
    // write above isn't reactive on its own. `replace` keeps this off the
    // back-button history; the path/search are unchanged so this is a no-op
    // navigation everywhere except re-running that one theme read.
    navigate(`${location.pathname}${location.search}`, { replace: true });
  };

  const logoClick = () => {
    navigate(ROUTES.DASHBOARD);
    if (isMobile) {
      setOpenSideBar(false);
    }
  };

  const productTile = (
    product: TProductName,
    size: number,
    iconSize: number
  ) => {
    const isSelected = product === selectedProduct;
    return (
      <Box
        sx={{
          width: size,
          height: size,
          flexShrink: 0,
          borderRadius: theme.radius.md,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: isSelected
            ? theme.palette.primary.main
            : theme.palette.background.subtle,
          transition: 'background-color 0.15s ease'
        }}
      >
        <Icon
          name="planListHeader"
          product={product}
          title={t(PRODUCT_LABEL_KEY[product])}
          size={iconSize}
          color={
            isSelected
              ? theme.palette.primary.contrastText
              : theme.palette.text.secondary
          }
        />
      </Box>
    );
  };

  const productPicker = () => {
    return (
      <Stack gap="12px">
        <Typography
          variant="labelSmall"
          color="text.secondary"
          sx={SECTION_LABEL_SX}
        >
          {t('chooseProduct')}
        </Typography>
        <Box sx={{ display: 'flex', gap: '10px' }}>
          {PRODUCTS.map((product) => {
            const isSelected = product === selectedProduct;
            return (
              <Stack
                key={product}
                alignItems="center"
                gap="6px"
                sx={{
                  flex: 1,
                  minWidth: 0,
                  cursor: 'pointer',
                  WebkitTapHighlightColor: 'transparent'
                }}
                onClick={() => onProductSelect(product)}
              >
                {productTile(product, 48, 22)}
                <Typography
                  variant="bodySmall"
                  noWrap
                  color={isSelected ? 'primary.main' : 'text.secondary'}
                  fontWeight={isSelected ? 600 : 400}
                >
                  {t(PRODUCT_LABEL_KEY[product])}
                </Typography>
              </Stack>
            );
          })}
        </Box>
      </Stack>
    );
  };

  const viewingBanner = () => {
    return (
      <Stack
        direction="row"
        alignItems="center"
        gap="12px"
        sx={{
          marginTop: '20px',
          padding: '12px',
          borderRadius: theme.radius.md,
          backgroundColor: theme.palette.primary.tintedSurface,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.35)}`
        }}
      >
        {productTile(selectedProduct, 40, 18)}
        <Stack gap="2px">
          <Typography
            variant="labelSmall"
            color="primary.main"
            sx={SECTION_LABEL_SX}
          >
            {t('viewing')}
          </Typography>
          <Typography
            variant="titleSmall"
            fontWeight={700}
            color="text.primary"
          >
            {t(PRODUCT_LABEL_KEY[selectedProduct])}
          </Typography>
        </Stack>
      </Stack>
    );
  };

  const list = (navData: TNavData_SideBar[], sectionLabel?: string) => {
    return (
      <Stack gap="4px">
        {sectionLabel && (
          <Typography
            variant="labelSmall"
            color="text.secondary"
            sx={{ ...SECTION_LABEL_SX, marginBottom: '4px' }}
          >
            {sectionLabel}
          </Typography>
        )}
        <List
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '4px'
          }}
        >
          {navData.map((nav) => {
            const isSelected = selectedNav?.name === nav.name;
            const NavIcon = nav.icon;
            return (
              <ListItem key={nav?.name} disablePadding>
                <ListItemButton
                  selected={isSelected}
                  sx={{
                    borderRadius: theme.radius.md,
                    paddingBlock: '10px',
                    paddingLeft: '16px',
                    gap: '12px',
                    position: 'relative',
                    transition: 'background-color 0.15s ease',
                    WebkitTapHighlightColor: 'transparent',
                    '&.Mui-selected, &.Mui-selected:hover': {
                      backgroundColor: theme.palette.primary.tintedSurface,
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: 0,
                        top: '18%',
                        bottom: '18%',
                        width: '3px',
                        borderRadius: '0 4px 4px 0',
                        backgroundColor: theme.palette.primary.main
                      }
                    },
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover
                    }
                  }}
                  onClick={() => onSideNavClick(nav)}
                >
                  {NavIcon && (
                    <NavIcon
                      sx={{
                        fontSize: 20,
                        color: isSelected
                          ? theme.palette.primary.main
                          : theme.palette.text.secondary
                      }}
                    />
                  )}
                  <ListItemText
                    primary={nav.name}
                    primaryTypographyProps={{
                      variant: 'bodyLarge',
                      color: isSelected ? 'text.primary' : 'text.secondary',
                      fontWeight: isSelected ? 600 : 400
                    }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Stack>
    );
  };

  if (location.pathname === ROUTES.ADD_CAMERA_INFO || location.pathname === ROUTES.ADD_HUB_INFO) {
    return null;
  }

  return (
    <Drawer
      sx={{
        color: (theme) => theme.palette.text.primary,
        width: drawerWidth,
        position: 'absolute',
        height: '100vh',
        top: '0px',
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          backgroundColor: (theme) => theme.palette.background.default,
          backgroundImage: 'none',
          // Desktop rail: a hairline seam against the page on its right edge
          // plus the new-theme sheet depth. Mobile is a full-width sheet, so
          // no seam. Replaces MUI's hard elevation shadow.
          borderRight: isMobile
            ? 'none'
            : (theme) => `1px solid ${theme.palette.border.divider}`,
          boxShadow: (theme) => theme.palette.shadows.sheet
        }
      }}
      open={openSideBar}
      variant="persistent"
      anchor="left"
      elevation={0}
    >
      <DrawerHeader>
        {isMobile ? (
          <IconButton
            onClick={() => setOpenSideBar(false)}
            aria-label="close menu"
            sx={{
              p: 1,
              color: 'primary.main',
              bgcolor: (theme) => theme.palette.primary.tintedSurface,
              borderRadius: (theme) => theme.radius.full,
              '&:hover': {
                bgcolor: (theme) => theme.palette.primary.tintedSurface
              }
            }}
          >
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        ) : (
          <div />
        )}
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,
        jsx-a11y/no-noninteractive-element-interactions */}
        <img
          src={Logo}
          alt="logo"
          width="60"
          height="60"
          onClick={logoClick}
          style={{ ...logoContrastStyle, cursor: 'pointer' }}
        />
        <div style={{ width: '40px' }} />
      </DrawerHeader>
      <SideBarNav>
        <Stack gap="20px">
          {productPicker()}
          {viewingBanner()}
          {list(productNavData, t('menu'))}
          <Divider />
          {list(globalNavData, t('account'))}
        </Stack>
      </SideBarNav>
    </Drawer>
  );
}
export default SideBar;

const SideBarNav = styledMUI('div')`
  width: 100%;
  padding: 24px;
`;

const Divider = styledMUI('div')(({ theme }) => ({
  height: 1,
  backgroundColor: theme.palette.border.divider
}));
